import React, { Children } from 'react';
import classNames from 'classnames';
import PropTypes from 'prop-types';

/**
 * The main goal of `SelectionControlLabel` is to provide the `label` prop for its child components:
 * `Checkbox`, `Toggle`, `Radio`. It also can provide the values for the `value`, `disabled` or `checked`
 * props.
 *
 * See related components: [FormGroup](#fromgroup), [Checkbox](#checkbox), [Toggle](#toggle), [Radio](#radio)
 *
 * Usage:
 *
 * ```jsx
 * import { SelectionControlLabel } from '@one-thd/sui-atomic-components';
 * ```
 */
const SelectionControlLabel = (props) => {
  const { children, label, className, disabled: disabledProp } = props;
  const control = Children.only(children);

  let disabled = disabledProp;
  if (typeof disabled === 'undefined' && typeof control.props.disabled !== 'undefined') {
    disabled = control.props.disabled;
  }

  let controlProps = { disabled };
  let labelProps = props;
  ['checked', 'name', 'onChange', 'value', 'inputRef'].forEach((forwardProp) => {
    if (typeof control.props[forwardProp] === 'undefined' && typeof labelProps[forwardProp] !== 'undefined') {
      controlProps[forwardProp] = labelProps[forwardProp];
    }
  });

  const classes = classNames('sui-inline-flex sui-items-center sui-align-middle sui-ml-[-7px] sui-mr-[16px]', {
    'sui-text-primary sui-cursor-pointer sui-tap-highlight-transparent': !disabled,
    'sui-text-inactive sui-pointer-events-none': disabled
  }, className);

  const LabelRoot = 'label';
  return (
    <LabelRoot className={classes}>
      {control && React.cloneElement(control, controlProps)}
      <span className="sui-font-regular sui-text-base sui-leading-tight sui-m-0">{label}</span>
    </LabelRoot>
  );
};

SelectionControlLabel.displayName = 'SelectionControlLabel';

SelectionControlLabel.propTypes = {
  /**
   * @ignore
   */
  children: PropTypes.node.isRequired,
  /**
   * @ignore
   */
  className: PropTypes.string,
  /**
   * label after the associated control
   */
  label: PropTypes.string,
  /**
   * If true, control and label disabled
   */
  disabled: PropTypes.bool
};

SelectionControlLabel.defaultProps = {
  label: null,
  disabled: undefined,
  className: null
};

export { SelectionControlLabel };
